import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { useTheme } from 'styled-components';
import { DefaultTheme } from 'styled-components';
import { themeLight } from '../../styles/theme';
import { Toaster } from 'react-hot-toast';
import Sidebar from './Sidebar';
import Header from './Header';

const LayoutContainer = styled.div`
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  overflow: hidden;
`;

const MainContent = styled(motion.main)<{ $sidebarCollapsed: boolean }>`
  position: fixed;
  top: 72px;
  left: ${props => props.$sidebarCollapsed ? '72px' : '280px'};
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: ${({ theme }) => theme.spacing[4]};
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden;
  will-change: left;
  width: calc(100% - ${props => props.$sidebarCollapsed ? '72px' : '280px'});
  display: flex;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};
    transition: background 0.3s;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    left: 0 !important;
    width: 100% !important;
    top: 64px;
    padding: ${({ theme }) => theme.spacing[3]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    top: 60px;
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

// Faixa decorativa entre a sidebar e o conteúdo principal para dar o efeito "duas cores" no fundo
const BackgroundStrip = styled.div<{ $sidebarCollapsed: boolean; $useLight?: boolean }>`
  position: fixed;
  top: 72px;
  left: ${props => props.$sidebarCollapsed ? '72px' : '280px'};
  width: calc(100% - ${props => props.$sidebarCollapsed ? '72px' : '280px'});
  height: calc(100vh - 72px);
  pointer-events: none;
  z-index: 0;
  background: ${props => props.$useLight ? `${themeLight.colors.background}` : 'transparent'};
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$useLight ? 'inset 6px 0 18px rgba(255,255,255,0.06)' : 'none'};
  will-change: left, width;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    left: 0 !important;
    width: 100% !important;
    top: 64px;
    height: calc(100vh - 64px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    top: 60px;
    height: calc(100vh - 60px);
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 0 ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 ${({ theme }) => theme.spacing[6]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const PageTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.gradientNeural};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const BreadcrumbItem = styled.span<{ $active?: boolean }>`
  color: ${props => props.$active ? props.theme.colors.textPrimary : props.theme.colors.textTertiary};
  font-weight: ${props => props.$active ? props.theme.typography.fontWeight.medium : props.theme.typography.fontWeight.normal};
`;

const BreadcrumbSeparator = styled.span`
  color: ${({ theme }) => theme.colors.border};
`;

const LoadingOverlay = styled(motion.div)<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${({ theme }) => theme.colors.border};
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const LoadingText = styled.p`
  margin-top: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

interface LayoutProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
  loading?: boolean;
  loadingText?: string;
}

const Layout: React.FC<LayoutProps> = ({
  title,
  subtitle,
  breadcrumbs,
  loading = false,
  loadingText = 'Carregando...'
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const currentTheme = useTheme() as DefaultTheme;
  const useLight = currentTheme?.colors?.background === themeLight.colors.background;

  return (
    <LayoutContainer>
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
    <Header sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
  {/* Faixa de fundo decorativa que cria o efeito de duas cores entre sidebar e conteúdo */}
  <BackgroundStrip $sidebarCollapsed={sidebarCollapsed} $useLight={useLight} />

      
      <MainContent
        $sidebarCollapsed={sidebarCollapsed}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <ContentWrapper>
          {(title || subtitle || breadcrumbs) && (
            <PageHeader>
              {breadcrumbs && (
                <Breadcrumb>
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                      <BreadcrumbItem $active={index === breadcrumbs.length - 1}>
                        {item.label}
                      </BreadcrumbItem>
                      {index < breadcrumbs.length - 1 && (
                        <BreadcrumbSeparator>/</BreadcrumbSeparator>
                      )}
                    </React.Fragment>
                  ))}
                </Breadcrumb>
              )}
              
              {title && <PageTitle>{title}</PageTitle>}
              {subtitle && <PageSubtitle>{subtitle}</PageSubtitle>}
            </PageHeader>
          )}
          
          <Outlet />
        </ContentWrapper>
      </MainContent>

  <LoadingOverlay $visible={loading}>
        <div style={{ textAlign: 'center' }}>
          <LoadingSpinner />
          <LoadingText>{loadingText}</LoadingText>
        </div>
      </LoadingOverlay>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: currentTheme.colors.backgroundSecondary,
            color: currentTheme.colors.textPrimary,
            border: `1px solid ${currentTheme.colors.border}`,
            borderRadius: currentTheme.borders.radius.lg,
            boxShadow: currentTheme.shadows.lg,
          },
          success: {
            iconTheme: {
              primary: currentTheme.colors.success,
              secondary: currentTheme.colors.backgroundSecondary,
            },
          },
          error: {
            iconTheme: {
              primary: currentTheme.colors.error,
              secondary: currentTheme.colors.backgroundSecondary,
            },
          },
        }}
      />
    </LayoutContainer>
  );
};

export default Layout;

